import { useState, useCallback, useRef } from "react";
import { fetchPost } from "../utils/axios.tsx";
import {
  acquireAudioElement,
  playbackVolume,
  resetAudioElement,
} from "../voice/audioPlayback.ts";

type TTSState = "idle" | "loading" | "playing";

export interface SpeakOptions {
  /**
   * Fired once when playback actually finishes.
   *
   * The promise returned by `speak` settles when the audio has finished
   * *downloading*, which is earlier - fine for a listen button, but voice mode
   * would re-arm the microphone while Tally is still talking. It is not called
   * when playback is stopped by the caller (e.g. barge-in).
   */
  onEnded?: () => void;
  /**
   * Fired once when the audio starts playing - from then on it can be heard.
   * Before, the request may still be waiting on its first audio.
   */
  onStarted?: () => void;
  /**
   * Audio already requested with `requestSpeech`, played instead of fetching
   * the text again. Its fetch is aborted along with playback.
   */
  request?: SpeechRequest;
}

/** A speech request started ahead of playing it. */
export interface SpeechRequest {
  response: Promise<Response>;
  controller: AbortController;
}

/**
 * Starts synthesising `text` without playing it.
 *
 * The speech service takes about a second to send its first audio. Asking for
 * the next piece while the current one plays hides that second, instead of
 * leaving it as silence between the two. The caller must hand the request to
 * `speak`, or abort it.
 */
export function requestSpeech(
  exchangeID: string | undefined,
  text: string,
): SpeechRequest {
  const controller = new AbortController();
  const response = fetchPost(
    "/v1/text-to-speech/",
    { text, exchange_id: exchangeID },
    { signal: controller.signal },
  );
  // A request aborted before it is played is never awaited.
  response.catch(() => {});
  return { response, controller };
}

export function useTextToSpeech() {
  const [state, setState] = useState<TTSState>("idle");
  const mediaSourceRef = useRef<MediaSource | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const sourceBufferRef = useRef<SourceBuffer | null>(null);
  const isStoppedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  /**
   * Which utterance is current. Every `speak` or `playClip` takes the next
   * number and every stop moves it on, so a handler can tell that its own
   * utterance has ended even after the next one has started.
   */
  const callRef = useRef(0);

  const stop = useCallback(() => {
    // Ends the current utterance: its handlers see they are no longer current
    // and do nothing, so a caller-initiated stop is not a natural end.
    callRef.current++;
    if (isStoppedRef.current) return;

    isStoppedRef.current = true;

    // Abort any ongoing fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Clean up audio. The element is shared and stays alive across utterances
    // so it keeps its autoplay activation - only its source is detached.
    if (audioRef.current) {
      resetAudioElement();
      audioRef.current = null;
    }

    // Clean up MediaSource
    if (mediaSourceRef.current) {
      const mediaSource = mediaSourceRef.current;
      try {
        if (mediaSource.readyState !== "closed") {
          mediaSource.endOfStream();
        }
      } catch (err) {
        // Ignore errors during cleanup
      }

      mediaSourceRef.current = null;
    }

    // Clean up SourceBuffer
    sourceBufferRef.current = null;

    isStoppedRef.current = false;

    // Update state
    setState("idle");
  }, []);

  const speak = useCallback(
    async (
      exchangeID: string | undefined,
      text: string,
      options: SpeakOptions = {},
    ) => {
      // Stop any current playback
      stop();

      // The handlers below belong to this call alone. The element and refs
      // are shared with whatever is spoken next, so a handler that fires after
      // this call was stopped must not act: it would take the next
      // utterance's only play attempt, and that one would never start or end.
      const call = ++callRef.current;
      const stopped = () => callRef.current !== call;
      let playAttempted = false;
      // This call's own controller, so stopping it cancels its request too.
      const controller = options.request?.controller ?? new AbortController();
      abortControllerRef.current = controller;

      setState("loading");

      return new Promise<void>(async (resolve, reject) => {
        try {
          // Create MediaSource
          const mediaSource = new MediaSource();
          mediaSourceRef.current = mediaSource;

          // Reuse the shared, gesture-unlocked element. Creating one here would
          // be blocked by autoplay policy for every utterance voice mode speaks
          // after the first, since only entering voice mode is a user gesture.
          const audio = acquireAudioElement();
          audioRef.current = audio;
          audio.volume = playbackVolume();

          // Starts playback once, on the first chunk in the buffer.
          const startPlaying = async () => {
            if (playAttempted || stopped()) return;
            playAttempted = true;
            try {
              await audio.play();
            } catch (err) {
              // A play() cut short by this call's own stop is not a failure.
              if (!stopped()) console.error("Audio play failed:", err);
              return;
            }
            if (stopped()) return;
            setState("playing");
            options.onStarted?.();
          };

          // Create object URL
          const objectUrl = URL.createObjectURL(mediaSource);
          audio.src = objectUrl;

          const onSourceOpen = async () => {
            if (stopped()) {
              URL.revokeObjectURL(objectUrl);
              return;
            }

            try {
              // Create SourceBuffer
              const mimeType = "audio/mpeg";
              if (!MediaSource.isTypeSupported(mimeType)) {
                throw new Error(`MIME type ${mimeType} not supported`);
              }

              const sourceBuffer = mediaSource.addSourceBuffer(mimeType);
              sourceBufferRef.current = sourceBuffer;

              // Set up audio events
              audio.onended = () => {
                if (stopped()) return;
                stop();
                options.onEnded?.();
                resolve();
              };

              audio.onerror = () => {
                // Ignore empty src errors (code 4) which are expected during cleanup
                if (audio.error?.code === 4) {
                  return;
                }

                console.error("Audio element error:", audio.error);
                if (!stopped()) {
                  stop();
                  reject(new Error(`Audio error: ${audio.error?.message}`));
                }
              };

              // Set up SourceBuffer events
              sourceBuffer.onerror = () => {
                if (!stopped()) {
                  stop();
                  reject(new Error("SourceBuffer error"));
                }
              };

              // Fetch audio stream
              const response = await (options.request?.response ??
                fetchPost(
                  "/v1/text-to-speech/",
                  {
                    text,
                    exchange_id: exchangeID,
                  },
                  {
                    signal: controller.signal,
                  },
                ));

              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }

              if (!response.body) {
                throw new Error("Response has no body");
              }

              const reader = response.body.getReader();
              let bufferQueue: Uint8Array[] = [];
              let isProcessing = true;
              let totalBytes = 0;

              // Function to safely append buffer
              const safeAppendBuffer = (chunk: Uint8Array) => {
                if (stopped() || !sourceBuffer || sourceBuffer.updating) {
                  return false;
                }

                try {
                  sourceBuffer.appendBuffer(chunk);
                  return true;
                } catch (err) {
                  // Ignore errors if we're stopping
                  if (!stopped()) {
                    console.error("Failed to append buffer:", err);
                  }
                  return false;
                }
              };

              // Function to process queue
              const processQueue = () => {
                if (stopped() || !sourceBuffer || bufferQueue.length === 0) {
                  return;
                }

                if (!sourceBuffer.updating) {
                  const chunk = bufferQueue.shift()!;
                  const appended = safeAppendBuffer(chunk);

                  if (!appended) {
                    bufferQueue.unshift(chunk);
                  }
                }
              };

              // Ends the stream once the download is over and every chunk is
              // in the buffer. Ending it earlier fails: `endOfStream` throws
              // while an append is in flight, and chunks still queued would
              // reopen the stream. Either way it is never ended, so playback
              // stalls on the last frame and `ended` - Tally's cue that it has
              // stopped talking - never fires.
              let downloadDone = false;
              const endStreamIfDrained = () => {
                if (
                  !downloadDone ||
                  stopped() ||
                  bufferQueue.length > 0 ||
                  sourceBuffer.updating ||
                  mediaSource.readyState !== "open"
                ) {
                  return;
                }
                try {
                  mediaSource.endOfStream();
                } catch (err) {
                  console.error("Failed to end audio stream:", err);
                }
              };

              // Process stream
              const processStream = async () => {
                try {
                  while (isProcessing && !stopped()) {
                    const { done, value } = await reader.read();

                    if (done || stopped()) {
                      isProcessing = false;
                      downloadDone = true;
                      endStreamIfDrained();

                      if (!stopped()) {
                        resolve();
                      }

                      break;
                    }

                    if (value && !stopped()) {
                      totalBytes += value.byteLength;

                      // Add to queue
                      bufferQueue.push(value);

                      // Process queue
                      processQueue();
                    }
                  }
                } catch (err) {
                  // Check if it's an AbortError
                  if (err instanceof Error && err.name === "AbortError") {
                    // Stream was aborted intentionally
                    return;
                  }

                  console.error("Error processing stream:", err);
                  if (!stopped()) {
                    stop();
                    reject(err);
                  }
                }
              };

              // Handle sourceBuffer update events
              sourceBuffer.onupdateend = () => {
                if (stopped()) return;

                // Process next item in queue
                processQueue();
                endStreamIfDrained();

                // Start on the first appended chunk. TTS streams faster than
                // real time, so waiting for a buffer only adds silence before
                // the first word - and a short clip may never reach one.
                if (totalBytes > 0) void startPlaying();
              };

              // Start processing the stream
              processStream();
            } catch (err) {
              // Stopped on purpose (by the caller, or by the next utterance
              // starting): not an error, and stopping again here would cut off
              // whatever is playing now.
              if (controller.signal.aborted) return;
              console.error("Error in sourceopen handler:", err);
              if (!stopped()) {
                stop();
                reject(err);
              }
            }
          };

          const onSourceError = () => {
            if (!stopped()) {
              stop();
              reject(new Error("MediaSource error"));
            }
          };

          mediaSource.addEventListener("sourceopen", onSourceOpen);
          mediaSource.addEventListener("error", onSourceError);

          // Cleanup function for this speak call
          const cleanup = () => {
            mediaSource.removeEventListener("sourceopen", onSourceOpen);
            mediaSource.removeEventListener("error", onSourceError);
            URL.revokeObjectURL(objectUrl);
          };

          // Auto-cleanup after completion or error
          const originalResolve = resolve;
          const originalReject = reject;

          resolve = () => {
            cleanup();
            originalResolve();
          };

          reject = (err: any) => {
            cleanup();
            originalReject(err);
          };
        } catch (err) {
          console.error("Error in speak function:", err);
          if (!stopped()) {
            stop();
            reject(err);
          }
        }
      });
    },
    [stop],
  );

  /** Plays an already-synthesised clip on the shared element. */
  const playClip = useCallback(
    async (clip: Blob, options: SpeakOptions = {}) => {
      stop();
      const call = ++callRef.current;
      const audio = acquireAudioElement();
      audioRef.current = audio;
      audio.volume = playbackVolume();
      // `stop` resets the element, which also revokes this object URL and
      // detaches this handler - so a caller's stop does not count as an end.
      audio.onended = () => {
        stop();
        options.onEnded?.();
      };
      audio.src = URL.createObjectURL(clip);
      setState("playing");
      try {
        await audio.play();
        if (callRef.current === call) options.onStarted?.();
      } catch (err) {
        // Only stop the clip if it is still the one playing. Interrupted while
        // starting, it has been replaced, and stopping would cut off whatever
        // replaced it.
        if (callRef.current === call) stop();
        throw err;
      }
    },
    [stop],
  );

  return {
    speak,
    playClip,
    stop,
    state,
    isPlaying: state === "playing",
    isLoading: state === "loading",
  };
}
